import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { useNotify } from 'react-admin';
import { DialogCloseButton } from '@/components/misc/DialogCloseButton';
import {
    downloadQuoteAttachment,
    fetchQuoteAttachmentBlob,
    attachmentPreviewKind,
    QuoteAttachment,
} from '@/utils/quote_attachment_utils';

type Props = {
    attachment: QuoteAttachment | null;
    /** SharePoint / external URL — preview without quote-attachments API. */
    sourceUrl?: string | null;
    /** Parent is still fetching the blob (e.g. receipt content). */
    loading?: boolean;
    onClose: () => void;
};


export function QuoteAttachmentPreviewDialog({
    attachment,
    sourceUrl = null,
    loading: externalLoading = false,
    onClose,
}: Props) {
    const notify = useNotify();
    const [previewSrc, setPreviewSrc] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [mediaReady, setMediaReady] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        if (!attachment) {
            setPreviewSrc(null);
            setLoading(false);
            setMediaReady(false);
            return undefined;
        }

        if (sourceUrl) {
            setPreviewSrc(sourceUrl);
            setLoading(false);
            setMediaReady(false);
            return undefined;
        }

        // Parent is fetching a blob URL (e.g. receipt content). Wait for
        // sourceUrl — do not hit /quote-attachments/{id} with a fake id.
        if (externalLoading) {
            setPreviewSrc(null);
            setLoading(false);
            setMediaReady(false);
            return undefined;
        }

        let objectUrl: string | null = null;
        let cancelled = false;
        setLoading(true);
        setMediaReady(false);
        setPreviewSrc(null);

        fetchQuoteAttachmentBlob(attachment)
            .then(blob => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setPreviewSrc(objectUrl);
            })
            .catch(e => {
                if (cancelled) return;
                notify(
                    e instanceof Error ? e.message : 'Failed to load attachment',
                    { type: 'error' },
                );
                setPreviewSrc(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [attachment, sourceUrl, externalLoading, notify]);

    const handleDownload = async () => {
        if (!attachment) return;
        setDownloading(true);
        try {
            if (sourceUrl) {
                const link = document.createElement('a');
                link.href = sourceUrl;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.download = attachment.filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
            } else {
                await downloadQuoteAttachment(attachment);
            }
        } catch (e) {
            notify(
                e instanceof Error ? e.message : 'Download failed',
                { type: 'error' },
            );
        } finally {
            setDownloading(false);
        }
    };

    const title = attachment?.name || attachment?.filename || 'Preview';
    const previewKind = attachment
        ? attachmentPreviewKind(attachment.content_type, attachment.filename)
        : 'other';
    const showImage = previewKind === 'image';
    const showPdf = previewKind === 'pdf';
    const waitingForMedia =
        !!previewSrc && (showImage || showPdf) && !mediaReady;
    const showSpinner = externalLoading || loading || waitingForMedia;

    return (
        <Dialog open={!!attachment} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                {title}
                <DialogCloseButton onClose={onClose} />
            </DialogTitle>
            <DialogContent
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 240,
                    pb: 2,
                    position: 'relative',
                }}
            >
                {showSpinner && (
                    <CircularProgress
                        size={32}
                        sx={{
                            position: previewSrc ? 'absolute' : 'static',
                            my: previewSrc ? 0 : 4,
                        }}
                    />
                )}
                {!externalLoading && !loading && !previewSrc ? (
                    <Typography color="text.secondary">
                        Could not load preview.
                    </Typography>
                ) : showImage && previewSrc ? (
                    <Box
                        component="img"
                        src={previewSrc}
                        alt={title}
                        onLoad={() => setMediaReady(true)}
                        onError={() => setMediaReady(true)}
                        sx={{
                            display: mediaReady ? 'block' : 'none',
                            maxWidth: '100%',
                            maxHeight: '80vh',
                            objectFit: 'contain',
                        }}
                    />
                ) : showPdf && previewSrc ? (
                    <Box
                        component="iframe"
                        src={previewSrc}
                        title={title}
                        onLoad={() => setMediaReady(true)}
                        sx={{
                            display: mediaReady ? 'block' : 'none',
                            width: '100%',
                            height: '80vh',
                            border: 0,
                        }}
                    />
                ) : !showSpinner && previewSrc ? (
                    <Typography color="text.secondary">
                        Preview is not available for this file type. Use Download.
                    </Typography>
                ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>Close</Button>
                <Button
                    variant="contained"
                    startIcon={<DownloadOutlinedIcon />}
                    onClick={() => void handleDownload()}
                    disabled={
                        !attachment ||
                        downloading ||
                        externalLoading ||
                        (!sourceUrl && !previewSrc)
                    }
                >
                    Download
                </Button>
            </DialogActions>
        </Dialog>
    );
}
